import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { supabase } from "../../config/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { getCustomerById, getJobsByCustomer, getOtherCustomerNotesByName } from "../../lib/supabase/customers";
import TextTooltip from "../common/TextTooltip";

import { marked } from "marked";
import DOMPurify from "dompurify";

import {
    FiSearch, FiX, FiChevronDown, FiCheckCircle, FiRefreshCw,
    FiPhone, FiMail, FiMapPin, FiTool,
    FiArrowLeft, FiAlertCircle, FiCalendar, FiChevronRight,
    FiEdit2, FiCheck, FiSend, FiPause,
} from "react-icons/fi";
import {
    Home, PanelsTopLeft,
    ArrowDownNarrowWide, ArrowUpNarrowWide, ArrowDownAZ, ArrowUpAZ, Flame, Briefcase,
    NotebookPen,
    SquareStack,
} from "lucide-react";
import { GiWoodBeam, GiWoodPile } from "react-icons/gi";
import { HiOutlineSquare3Stack3D } from "react-icons/hi2";

// ─── Constants ─────────────────────────────────────────────────────────────────
const PAGE_LIMIT = 50;

const STORAGE_KEYS = {
    sortBy: 'single_customer_sortBy',
    statusFilter: 'single_customer_statusFilter',
    jobTypeFilter: 'single_customer_jobTypeFilter',
};

const CUSTOMER_TYPE_META = {
    Account: "bg-blue-50 text-blue-700 border-blue-200",
    COD: "bg-amber-50 text-amber-700 border-amber-200",
};

const load = (key, def) => {
    try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : def; }
    catch { return def; }
};
const save = (key, val) => {
    try { localStorage.setItem(key, JSON.stringify(val)); } catch { }
};

const renderMarkdown = (text) => {
    const html = marked.parse(text || "", {
        breaks: true,
        gfm: true,
    });

    return DOMPurify.sanitize(html);
};

// ─── Status / quote meta ───────────────────────────────────────────────────────
const STATUS_META = {
    pending_design: { label: "Pending Design", dot: "bg-amber-400" },
    in_design: { label: "In Design", dot: "bg-blue-400" },
    design_completed: { label: "Design Completed", dot: "bg-emerald-400" },
    pending_estimation: { label: "Pending Estimation", dot: "bg-orange-400" },
    in_estimation: { label: "In Estimation", dot: "bg-violet-400" },
    estimation_completed: { label: "Estimation Completed", dot: "bg-green-400" },
    ready_for_delivery: { label: "Ready for Delivery", dot: "bg-teal-400" },
    completed: { label: "Completed", dot: "bg-gray-400" },
};
const getStatusMeta = (s) => STATUS_META[s] || { label: s?.replace(/_/g, " ") || "—", dot: "bg-gray-300" };

// design_status → icon bubble color
const DS_STYLE = {
    completed: { bg: "bg-emerald-500", text: "text-white" },
    in_progress: { bg: "bg-amber-400", text: "text-white" },
    waiting_design: { bg: "bg-blue-500", text: "text-white" },
    waiting_assignment: { bg: "bg-gray-300", text: "text-gray-600" },
    not_required: { bg: "bg-gray-100", text: "text-gray-300" },
};
const getDSStyle = (s) => DS_STYLE[s] || DS_STYLE.waiting_assignment;

// 3-color status system: gray / orange / green
const STATUS_TIER = (s) => {
    if (!s || s === 'not_required' || s === 'not_started' || s === 'waiting_assignment') return 'gray';
    if (s === 'completed') return 'green';
    return 'orange'; // in_progress, waiting_design, in_estimation, in_cutting, cutting_ended, in_production
};
const STATUS_CHIP_CLS = {
    gray: "bg-gray-100 text-gray-400 border-gray-200",
    orange: "bg-orange-50 text-orange-500 border-orange-200",
    green: "bg-emerald-50 text-emerald-600 border-emerald-200",
};
const getStatusChipCls = (s) => STATUS_CHIP_CLS[STATUS_TIER(s)];

const SORT_OPTIONS = [
    { value: "newest", label: "Newest First", icon: ArrowDownNarrowWide },
    { value: "oldest", label: "Oldest First", icon: ArrowUpNarrowWide },
    { value: "priority", label: "Priority", icon: Flame },
    { value: "alphabetical", label: "A → Z", icon: ArrowDownAZ },
    { value: "reverse_alphabetical", label: "Z → A", icon: ArrowUpAZ },
];

const QUOTE_ICONS = { beams: <GiWoodPile size={10} />, flooring_sheets: <SquareStack size={10} />, roof: <Home size={10} />, wall: <PanelsTopLeft size={10} />, floor: <HiOutlineSquare3Stack3D size={10} />, timber: <GiWoodBeam size={10} /> };
const QUOTE_ORDER = { beams:0, flooring_sheets: 1, roof: 2, wall: 3, floor: 4, timber: 5 };
const QUOTE_LABELS = { beams:"Beams", flooring_sheets: "Flooring Sheets", roof: "Roof", wall: "Wall", floor: "Floor", timber: "Timber" };


const formatDate = (ds) => {
    if (!ds) return "—";
    return new Date(ds).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Australian phone formatting + validation ─────────────────────────────────
const formatAustralianPhone = (raw) => {
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    const local = (digits.startsWith('61') && digits.length >= 11) ? '0' + digits.slice(2) : digits;
    if (/^04/.test(local)) {
        if (local.length <= 4) return local;
        if (local.length <= 7) return `${local.slice(0, 4)} ${local.slice(4)}`;
        return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7, 10)}`;
    }
    if (/^0[2378]/.test(local)) {
        if (local.length <= 2) return local;
        if (local.length <= 6) return `(${local.slice(0, 2)}) ${local.slice(2)}`;
        return `(${local.slice(0, 2)}) ${local.slice(2, 6)} ${local.slice(6, 10)}`;
    }
    if (/^1[389]00/.test(local)) {
        if (local.length <= 4) return local;
        if (local.length <= 7) return `${local.slice(0, 4)} ${local.slice(4)}`;
        return `${local.slice(0, 4)} ${local.slice(4, 7)} ${local.slice(7)}`;
    }
    if (/^13\d/.test(local) && local.length <= 6) {
        return local.length <= 2 ? local : `${local.slice(0, 2)} ${local.slice(2)}`;
    }
    return raw;
};

// Formats as user types — strips country code if pasted, caps at 10 local digits
const formatPhoneInput = (val) => {
    let digits = val.replace(/\D/g, '');
    if (digits.startsWith('61') && digits.length > 10) digits = '0' + digits.slice(2);
    digits = digits.slice(0, 10);
    return formatAustralianPhone(digits) || digits;
};

const isValidAustralianPhone = (val) => {
    if (!val || !val.trim()) return true;
    let d = val.replace(/\D/g, '');
    if (d.startsWith('61') && d.length === 11) d = '0' + d.slice(2);
    return /^(04\d{8}|0[2378]\d{8}|1[389]00\d{6}|13\d{4})$/.test(d);
};

// Converts a formatted local number back to E.164 (+61...) for storage
const toE164 = (val) => {
    if (!val || !val.trim()) return val;
    const digits = val.replace(/\D/g, '');
    if (digits.startsWith('61') && digits.length === 11) return '+' + digits;
    if (digits.startsWith('0') && digits.length === 10) return '+61' + digits.slice(1);
    return val; // 13XXXX / 1300 / 1800 — store as-is
};

// ─── Grid column template (shared by col-header + rows) ───────────────────────
const COL = "1.6fr 1.3fr 1fr 1.4fr 90px 24px";

// ─── Quote icon stack ─────────────────────────────────────────────────────────
const QuoteStack = ({ job }) => {
    const required = (job.job_quotes || []).filter(q => q.is_required);
    if (!required.length) return <span className="text-xs text-gray-300">—</span>;

    const byType = required.reduce((acc, q) => {
        (acc[q.quote_type] = acc[q.quote_type] || []).push(q);
        return acc;
    }, {});

    const flat = Object.entries(byType)
        .sort(([a], [b]) => (QUOTE_ORDER[a] ?? 99) - (QUOTE_ORDER[b] ?? 99))
        .flatMap(([type, quotes]) => quotes.map(q => ({ ...q, type })));

    const invoicedCount = flat.filter(q => q.invoiced).length;
    const uniqueTypes = [...new Set(flat.map(q => q.type))];

    return (
        <div className="flex items-center gap-2">
            <div className="flex -space-x-1.5">
                {flat.map((q, i) => {
                    const ds = getDSStyle(q.design_status);
                    return (
                        <div
                            key={i}
                            className="relative flex-shrink-0"
                            style={{ zIndex: flat.length - i }}
                            title={`${QUOTE_LABELS[q.type] || q.type} · ${q.design_status?.replace(/_/g, " ") || "pending"}${q.invoiced ? " · Invoiced" : ""}`}
                        >
                            <span className={`w-[22px] h-[22px] rounded-full flex items-center justify-center border-[1.5px] border-white ${ds.bg} ${ds.text}`}>
                                {QUOTE_ICONS[q.type]}
                            </span>
                            {q.invoiced && (
                                <span className="absolute -bottom-[2px] -right-[2px] w-[9px] h-[9px] bg-emerald-500 rounded-full border border-white" />
                            )}
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-col leading-none gap-0.5">
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-500 font-medium">
                        {flat.length} Dep{flat.length !== 1 && "s"}
                    </span>

                    <div className="flex gap-1">
                        {uniqueTypes.map((type, i) => (
                            <span
                                key={i}
                                className="px-1.5 py-[1px] text-[9px] rounded bg-gray-100 text-gray-600 border"
                                title={QUOTE_LABELS[type] || type}
                            >
                                {QUOTE_LABELS[type]?.[0] || type[0].toUpperCase()}
                            </span>
                        ))}
                    </div>
                </div>
                {invoicedCount > 0 && (
                    <span className="text-[10px] text-emerald-600 font-medium">
                        {invoicedCount === flat.length ? "All invoiced" : `${invoicedCount}/${flat.length} inv.`}
                    </span>
                )}
            </div>
        </div>
    );
};

// ─── EST / DES / PROD / DEL status boxes ──────────────────────────────────────
const StatusFields = ({ job }) => {
    const jobType = job.job_type || "";
    const req = (job.job_quotes || []).filter(q => q.is_required);

    const priority = ['in_progress', 'waiting_assignment', 'waiting_design', 'in_cutting',
        'cutting_ended', 'in_production', 'completed', 'not_required'];
    const agg = (key) => {
        const vals = req.map(q => q[key]).filter(Boolean);
        if (!vals.length) return null;
        return vals.reduce((best, cur) => {
            const bi = priority.indexOf(best), ci = priority.indexOf(cur);
            return (ci === -1 ? priority.length : ci) < (bi === -1 ? priority.length : bi) ? cur : best;
        });
    };

    const estStatus = jobType.includes("Estimation") ? agg("estimation_status") : null;
    const desStatus = (jobType.includes("Design") || jobType === "Timber") ? agg("design_status") : null;
    const prodStatus = (jobType.includes("Design") || jobType === "Timber") ? agg("production_status") : null;
    const delStatus = job.job_deliveries?.[0]?.status ?? null;

    const boxes = [
        { abbr: "EST", tooltip: `Estimation: ${(estStatus || "pending").replace(/_/g, " ")}`, status: estStatus, show: !!estStatus && estStatus !== "not_required" },
        { abbr: "DES", tooltip: `Design: ${(desStatus || "pending").replace(/_/g, " ")}`, status: desStatus, show: !!desStatus && desStatus !== "not_required" },
        { abbr: "PROD", tooltip: `Production: ${(prodStatus || "pending").replace(/_/g, " ")}`, status: prodStatus, show: !!prodStatus && prodStatus !== "not_required" && prodStatus !== "not_started" },
        { abbr: "DEL", tooltip: `Delivery: ${(delStatus || "pending").replace(/_/g, " ")}`, status: delStatus, show: !!delStatus && delStatus !== "not_required" },
    ].filter(b => b.show);

    if (!boxes.length) return <span className="text-[10px] text-gray-300">—</span>;

    return (
        <div className="flex flex-row gap-0.5 items-center content-center">
            {boxes.map((b, i) => (
                <TextTooltip key={i} text={b.tooltip} customStyle="!items-center !self-center !justify-center">
                    <span className={`inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded border leading-none tracking-wide ${getStatusChipCls(b.status)}`}>
                        {b.abbr}
                    </span>
                </TextTooltip>
            ))}
        </div>
    );
};

// ─── Job row ──────────────────────────────────────────────────────────────────
const JobRow = React.memo(({ job, companySlug, navigate, location, virtualIndex, measureElement }) => {
    const sm = getStatusMeta(job.main_status);
    const isUrgent = job.urgent_quote || job.urgent_design;

    return (
        <div
            onClick={() => navigate(`/${companySlug}/assignment/${job.id}`, {
                state: { from: location.pathname }
            })}
            ref={measureElement}
            data-index={virtualIndex}
            className="customer-job-row grid items-center gap-3 px-5 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors group"
            style={{ gridTemplateColumns: COL }}
        >
            {/* Col 1: ID + type + flags */}
            <div className="flex items-center gap-2 min-w-0">
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sm.dot}`} />
                <div className="min-w-0">
                    {/* Row 1: unique_id + flags */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-bold text-zinc-700">{job.unique_id}</span>
                        {job.on_hold && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded border border-gray-200 inline-flex items-center gap-0.5">
                                <FiPause size={7} /> Hold
                            </span>
                        )}
                        {isUrgent && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-orange-50 text-orange-500 rounded border border-orange-200 inline-flex items-center gap-0.5">
                                <FiAlertCircle size={7} /> Urgent
                            </span>
                        )}
                        {job.quote_sent && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-gray-50 text-gray-500 rounded border border-gray-200 inline-flex items-center gap-0.5">
                                <FiSend size={7} /> Sent
                            </span>
                        )}
                        {job.is_unit && job.unit_count > 0 && (
                            <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-gray-50 text-gray-600 rounded border border-gray-200">
                                {job.unit_count}u
                            </span>
                        )}
                    </div>
                    {/* Row 2: job type */}
                    {job.job_type && (
                        <p className="text-[10px] text-gray-400 mt-0.5">{job.job_type}</p>
                    )}
                    {/* Row 3: supervisor */}
                    {job.carpenter_supervisor_name && (
                        <p className="text-[10px] text-gray-400 truncate mt-0.5 flex items-center gap-1">
                            <FiTool size={8} className="flex-shrink-0" />
                            {job.carpenter_supervisor_name}
                        </p>
                    )}
                </div>
            </div>

            {/* Col 2: Quote stack */}
            <QuoteStack job={job} />

            {/* Col 3: Status */}
            <StatusFields job={job} />

            {/* Col 4: Address */}
            <div className="min-w-0">
                {job.customer_address ? (
                    <p className="text-[11px] text-gray-500 truncate flex items-center gap-1">
                        <FiMapPin size={9} className="flex-shrink-0 text-gray-300" />
                        {job.customer_address}
                    </p>
                ) : (
                    <span className="text-[11px] text-gray-300">—</span>
                )}
            </div>

            {/* Col 5: Date */}
            <span className="text-[11px] text-gray-400">{formatDate(job.created_at)}</span>

            {/* Col 6: Arrow */}
            <FiChevronRight size={13} className="text-gray-200 group-hover:text-gray-400 transition-colors" />
        </div>
    );
});

// ─── Editable field ───────────────────────────────────────────────────────────
const EditableField = ({ value, placeholder, type = "text", isPhone = false, className = "", textClassName = "", onSave, noteSection = false }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || "");
    const [saving, setSaving] = useState(false);
    const [phoneError, setPhoneError] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);
    useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);

    const start = () => { setDraft(isPhone ? (formatAustralianPhone(value) || value || "") : (value || "")); setEditing(true); setPhoneError(false); };
    const cancel = () => { setEditing(false); setPhoneError(false); };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneInput(e.target.value);
        setDraft(formatted);
        setPhoneError(false);
    };

    const submit = async () => {
        if (isPhone && !isValidAustralianPhone(draft)) {
            setPhoneError(true);
            return;
        }
        setSaving(true);
        const saveValue = isPhone ? toE164(draft) : draft;
        try { await onSave(saveValue); setEditing(false); setPhoneError(false); }
        catch (e) { console.error(e); }
        finally { setSaving(false); }
    };

    if (editing) {
        return (
            <div className={`flex flex-col gap-0.5 ${className}`}>
                <div className="flex items-center gap-1.5">
                    {!noteSection ?
                        <input
                            ref={inputRef}
                            type={isPhone ? "tel" : type}
                            value={draft}
                            onChange={isPhone ? handlePhoneChange : e => setDraft(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") submit(); if (e.key === "Escape") cancel(); }}
                            className={`text-sm bg-transparent border-b-2 focus:outline-none text-zinc-800 py-0.5 w-44 max-w-full ${phoneError ? "border-red-400" : "border-zinc-600"}`}
                            placeholder={isPhone ? "04XX XXX XXX" : placeholder}
                            inputMode={isPhone ? "numeric" : undefined}
                        />
                        :
                        <textarea
                            ref={inputRef}
                            type={isPhone ? "tel" : type}
                            value={draft}
                            onChange={isPhone ? handlePhoneChange : e => setDraft(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === "Escape") cancel();
                            }}
                            className={`text-sm resize-none bg-transparent border-b-2 focus:outline-none text-zinc-800 py-0.5 w-96 max-w-full ${phoneError ? "border-red-400" : "border-zinc-600"}`}
                            placeholder={isPhone ? "04XX XXX XXX" : placeholder}
                            inputMode={isPhone ? "numeric" : undefined}
                        />
                    }
                    <button
                        onClick={submit}
                        disabled={saving}
                        className="w-6 h-6 rounded-full bg-zinc-700 text-white flex items-center justify-center hover:bg-zinc-600 disabled:opacity-40 transition-colors flex-shrink-0"
                    >
                        {saving ? <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" /> : <FiCheck size={11} />}
                    </button>
                    <button
                        onClick={cancel}
                        className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
                    >
                        <FiX size={11} />
                    </button>
                </div>
                {phoneError && (
                    <p className="text-[10px] text-red-500 leading-none">Enter a valid Australian number (e.g. 0412 345 678)</p>
                )}
            </div>
        );
    }

    return (
        <div className={`group/f flex items-center gap-1.5 ${className}`}>
            {noteSection && value ? (
                <div className="bg-gray-100 rounded min-w-64 px-2">
                    <div
                        className={`${textClassName} prose prose-sm max-w-none prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-p:my-0 prose-li:leading-snug`}
                        dangerouslySetInnerHTML={{
                            __html: renderMarkdown(value)
                        }}
                    />
                </div>
            ) : (
                <span className={`${textClassName} whitespace-pre-wrap`}>
                    {isPhone
                        ? (formatAustralianPhone(value) || <span className="text-gray-300 italic text-sm">{placeholder}</span>)
                        : (value || <span className="text-gray-300 italic text-sm">{placeholder}</span>)
                    }
                </span>
            )}
            <button
                onClick={start}
                className="opacity-0 group-hover/f:opacity-100 transition-opacity w-5 h-5 rounded flex items-center justify-center text-gray-400 hover:text-zinc-700 hover:bg-gray-100 flex-shrink-0"
            >
                <FiEdit2 size={10} />
            </button>
        </div>
    );
};

const CustomerTypeSelector = ({ value, onSave }) => {
    const [saving, setSaving] = useState(false);
    const current = value || "None";
    const options = ["None", "Account", "COD"];

    const select = async (next) => {
        setSaving(true);
        try {
            await onSave(next === "None" ? null : next);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex items-center gap-2">
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-gray-100 p-1 border border-gray-200 w-64">
                {options.map(type => {
                    const active = current === type;

                    return (
                        <button
                            key={type}
                            type="button"
                            disabled={saving}
                            onClick={() => select(type)}
                            className={`rounded-lg px-3 py-0.5 text-xs font-semibold transition-all disabled:opacity-60 ${active
                                ? "bg-zinc-800 text-white shadow-sm"
                                : "text-gray-500 hover:text-zinc-800 hover:bg-white/70"
                                }`}
                        >
                            {type}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const OtherCustomerNotes = ({ notes = [] }) => {
    const [showAll, setShowAll] = useState(false);

    if (!notes.length) return null;

    const visibleNotes = showAll ? notes : notes.slice(0, 5);

    const getFromText = (note) => {
        if (note.email) return note.email;
        if (note.phone) return formatAustralianPhone(note.phone) || note.phone;
        return "other customer record";
    };

    return (
        <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-bold text-zinc-700 uppercase tracking-widest">
                    Other notes for same customer name
                </h3>
                <span className="text-[10px] text-gray-400">
                    {notes.length} note{notes.length !== 1 ? "s" : ""}
                </span>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                {visibleNotes.map(note => (
                    <div
                        key={note.id}
                        className="relative bg-gray-50 border border-gray-200 rounded-xl p-3 min-h-[102px] h-[102px] overflow-y-auto"
                    >
                        <div
                            className="prose prose-sm max-w-none prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-p:my-0 prose-li:leading-snug text-sm text-zinc-700 pb-2 pt-3"
                            dangerouslySetInnerHTML={{ __html: renderMarkdown(note.note) }}
                        />

                        <div className="absolute right-3 top-2 text-[10px] text-gray-400 font-medium">
                            From: {getFromText(note)}
                        </div>
                    </div>
                ))}
            </div>

            {notes.length > 5 && (
                <button
                    type="button"
                    onClick={() => setShowAll(prev => !prev)}
                    className="mt-3 text-xs font-semibold text-zinc-700 underline"
                >
                    {showAll ? "See less" : `Show more (${notes.length - 5})`}
                </button>
            )}
        </div>
    );
};
// ─── Main component ────────────────────────────────────────────────────────────
const SingleCustomer = () => {
    const { company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug, customerId } = useParams();

    // ── customer ──
    const [customer, setCustomer] = useState(null);
    const [customerLoading, setCustomerLoading] = useState(true);
    const [customerError, setCustomerError] = useState(null);

    // ── notes ──
    const [otherCustomerNotes, setOtherCustomerNotes] = useState([]);
    const [otherNotesLoading, setOtherNotesLoading] = useState(false);

    // ── jobs ──
    const [jobs, setJobs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: PAGE_LIMIT, total: 0, hasMore: false });

    // ── filters ──
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [sortBy, setSortBy] = useState(() => load(STORAGE_KEYS.sortBy, "newest"));
    const [statusFilter, setStatusFilter] = useState(() => load(STORAGE_KEYS.statusFilter, "all"));
    const [jobTypeFilter, setJobTypeFilter] = useState(() => load(STORAGE_KEYS.jobTypeFilter, "all"));
    const [openDropdown, setOpenDropdown] = useState(null);

    // ── refs ──
    const listRef = useRef(null);
    const debounceRef = useRef(null);
    const refSort = useRef(null);
    const refStatus = useRef(null);
    const refType = useRef(null);

    // ── debounce ──
    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 300);
        return () => clearTimeout(debounceRef.current);
    }, [searchQuery]);

    // ── persist filters ──
    useEffect(() => { save(STORAGE_KEYS.sortBy, sortBy); }, [sortBy]);
    useEffect(() => { save(STORAGE_KEYS.statusFilter, statusFilter); }, [statusFilter]);
    useEffect(() => { save(STORAGE_KEYS.jobTypeFilter, jobTypeFilter); }, [jobTypeFilter]);

    // ── load customer ──
    useEffect(() => {
        if (!customerId) return;
        setCustomerLoading(true);
        getCustomerById(customerId)
            .then(setCustomer)
            .catch(err => { console.error(err); setCustomerError("Customer not found."); })
            .finally(() => setCustomerLoading(false));
    }, [customerId]);

    // ── subscribe to customer changes ──
    useEffect(() => {
        if (!customerId) return;
        const ch = supabase
            .channel(`customer-detail-${customerId}`)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'customers', filter: `id=eq.${customerId}` },
                (payload) => setCustomer(prev => prev ? { ...prev, ...payload.new } : prev)
            )
            .subscribe();
        return () => supabase.removeChannel(ch);
    }, [customerId]);

    useEffect(() => {
        if (!company?.id || !customer?.name || !customerId) return;

        setOtherNotesLoading(true);

        getOtherCustomerNotesByName(company.id, customer.name, customerId)
            .then(setOtherCustomerNotes)
            .catch(err => {
                console.error(err);
                setOtherCustomerNotes([]);
            })
            .finally(() => setOtherNotesLoading(false));
    }, [company?.id, customer?.name, customerId]);

    // ── save any customer field ──
    const saveField = useCallback(async (field, value) => {
        if (field === 'email' && typeof value === 'string') {
            value = value.trim().toLowerCase();
        }

        let query = supabase
            .from('customers')
            .update({ [field]: value });

        if (field === 'customer_type' && customer?.name) {
            query = query
                .eq('company_id', company.id)
                .eq('name', customer.name);
        } else {
            query = query.eq('id', customerId);
        }

        const { error } = await query;
        if (error) throw error;

        setCustomer(prev => ({ ...prev, [field]: value }));
    }, [customerId, customer?.name, company?.id]);

    // ── fetch jobs ──
    const buildFilters = useCallback(() => ({
        searchQuery: debouncedQuery, sortBy, statusFilter, jobTypeFilter,
    }), [debouncedQuery, sortBy, statusFilter, jobTypeFilter]);

    const fetchJobs = useCallback(async () => {
        if (!company?.id || !customer?.name) return;
        setIsLoading(true);
        try {
            const result = await getJobsByCustomer(company.id, customer.name, 1, PAGE_LIMIT, buildFilters());
            setJobs(result.data);
            setPagination(result.pagination);
        } catch (err) { console.error(err); }
        finally { setIsLoading(false); }
    }, [company?.id, customer?.name, buildFilters]);

    const loadMore = useCallback(async () => {
        if (!company?.id || !customer?.name || isLoadingMore || !pagination.hasMore) return;
        setIsLoadingMore(true);
        try {
            const result = await getJobsByCustomer(company.id, customer.name, pagination.page + 1, PAGE_LIMIT, buildFilters());
            setJobs(prev => [...prev, ...result.data]);
            setPagination(result.pagination);
        } catch (err) { console.error(err); }
        finally { setIsLoadingMore(false); }
    }, [company?.id, customer?.name, isLoadingMore, pagination, buildFilters]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (customer?.name) fetchJobs(); }, [company?.id, customer?.name, debouncedQuery, sortBy, statusFilter, jobTypeFilter]);

    // ── jobs realtime ──
    useEffect(() => {
        if (!company?.id || !customer?.name) return;
        const ch = supabase
            .channel(`customer-jobs-${customerId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs', filter: `company_id=eq.${company.id}` },
                (payload) => {
                    const jName = payload.new?.customer_name ?? payload.old?.customer_name;
                    if (jName !== customer.name) return;
                    if (payload.eventType === 'INSERT') {
                        setJobs(prev => [payload.new, ...prev]);
                        setPagination(prev => ({ ...prev, total: prev.total + 1 }));
                    } else if (payload.eventType === 'UPDATE') {
                        setJobs(prev => prev.map(j => j.id === payload.new.id ? { ...j, ...payload.new } : j));
                    } else if (payload.eventType === 'DELETE') {
                        setJobs(prev => prev.filter(j => j.id !== payload.old.id));
                        setPagination(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
                    }
                }
            )
            .subscribe();
        return () => supabase.removeChannel(ch);
    }, [company?.id, customer?.name, customerId]);

    // ── infinite scroll ──
    useEffect(() => {
        const el = listRef.current;
        if (!el) return;
        const h = () => {
            if (isLoadingMore || !pagination.hasMore) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 400) loadMore();
        };
        el.addEventListener("scroll", h);
        return () => el.removeEventListener("scroll", h);
    }, [pagination.hasMore, isLoadingMore, loadMore]);

    // ── virtualizer (padding-spacer pattern) ──
    const rowVirtualizer = useVirtualizer({
        count: jobs.length,
        getScrollElement: () => listRef.current,
        estimateSize: () => 60,
        getItemKey: index => jobs[index].id,
        overscan: 8,
    });

    // ── derived ──
    const activeFilterCount = [statusFilter !== "all", jobTypeFilter !== "all", !!searchQuery].filter(Boolean).length;
    const clearAllFilters = () => { setStatusFilter("all"); setJobTypeFilter("all"); setSearchQuery(""); setSortBy("newest"); };
    const currentSort = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
    const SortIcon = currentSort.icon;
    const initials = (customer?.name || "?").split(" ").slice(0, 2).map(n => n[0] || "").join("").toUpperCase();

    // ── Loading / error ──
    if (customerLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <div className="w-6 h-6 border-2 border-zinc-700 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-gray-500">Loading customer…</p>
            </div>
        );
    }

    if (customerError || !customer) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-center gap-3">
                <p className="text-sm font-medium text-gray-700">{customerError || "Customer not found."}</p>
                <button onClick={() => navigate(`/${companySlug}/customers`, {
                    state: { from: location.pathname }
                })} className="text-xs font-semibold text-zinc-700 underline">
                    ← Back to Customers
                </button>
            </div>
        );
    }

    return (
        <div className="mobile-detail-page customer-detail-page flex flex-col gap-4 h-full min-h-0">

            {/* ── Back ─────────────────────────────────────────────────────── */}
            <button
                onClick={() => navigate(`/${companySlug}/customers`)}
                className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-zinc-700 flex-shrink-0 transition-colors w-fit font-medium"
            >
                <FiArrowLeft size={13} /> Back to Customers
            </button>

            {/* ── Customer header card ──────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md flex-shrink-0">
                <div className="customer-profile-header p-6 flex flex-col xl:flex-row items-start gap-6">

                    {/* Avatar with color picker */}
                    <div className="relative flex-shrink-0">
                        <div
                            className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold select-none bg-zinc-700"
                        >
                            {initials}
                        </div>
                    </div>

                    {/* Name + contact fields */}
                    <div className="flex-1 min-w-0">
                        {/* Name */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <EditableField
                                value={customer.name}
                                placeholder="Customer name"
                                textClassName="text-lg font-bold text-zinc-800 leading-snug"
                                onSave={v => saveField('name', v)}
                            />

                            {customer.customer_type && (
                                <span className={`px-2 py-0.5 rounded-full border text-[10px] font-black uppercase ${CUSTOMER_TYPE_META[customer.customer_type] || "bg-gray-50 text-gray-600 border-gray-200"
                                    }`}>
                                    {customer.customer_type}
                                </span>
                            )}
                        </div>
                        <p className="text-[11px] text-gray-400 mt-1 mb-4">
                            <FiCalendar size={9} className="inline mr-1 relative -top-px" />
                            Customer since {formatDate(customer.created_at)}
                        </p>

                        <div className="flex flex-col gap-2.5">
                            <div className="flex items-center gap-2">
                                <FiPhone size={12} className="text-gray-300 flex-shrink-0" />
                                <EditableField
                                    value={customer.phone}
                                    placeholder="Add phone number"
                                    isPhone
                                    className=""
                                    textClassName="text-sm text-zinc-700 font-medium"
                                    onSave={v => saveField('phone', v)}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <FiMail size={12} className="text-gray-300 flex-shrink-0" />
                                <EditableField
                                    value={customer.email}
                                    placeholder="Add email address"
                                    type="email"
                                    className=""
                                    textClassName="text-sm text-zinc-700 font-medium"
                                    onSave={v => saveField('email', v)}
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <Briefcase size={12} className="text-gray-300 flex-shrink-0" />
                                <CustomerTypeSelector
                                    value={customer.customer_type}
                                    onSave={v => saveField('customer_type', v)}
                                />
                            </div>

                            <div className="flex items-start gap-2 w-full max-w-xl">
                                <NotebookPen
                                    size={12}
                                    className="text-gray-300 flex-shrink-0 self-start mt-1"
                                />

                                <EditableField
                                    value={customer.note}
                                    placeholder="Add note for this customer"
                                    type="text"
                                    className=""
                                    textClassName="text-sm text-zinc-700 font-medium"
                                    onSave={v => saveField('note', v)}
                                    noteSection={true}
                                />
                            </div>

                            {otherNotesLoading ? (
                                <p className="text-xs text-gray-400 ml-5">
                                    Loading other notes…
                                </p>
                            ) : (
                                <OtherCustomerNotes notes={otherCustomerNotes} />
                            )}
                        </div>
                    </div>

                    {/* Total jobs */}
                    <div className="flex-shrink-0">
                        <div className="flex flex-col items-center px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl">
                            <span className="text-2xl font-bold text-zinc-700 leading-none">{pagination.total}</span>
                            <span className="text-[10px] text-gray-400 font-semibold uppercase tracking-widest mt-1">Total Jobs</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Jobs table card ───────────────────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md flex flex-col min-h-0 flex-shrink-0">

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0 flex-wrap gap-2">
                    <div>
                        <h2 className="text-[15px] font-bold text-zinc-700">Jobs</h2>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                            {pagination.total.toLocaleString()} jobs
                            {pagination.hasMore && <span className="ml-1 text-gray-300">· scroll for more</span>}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Search */}
                        <div className="relative">
                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={13} />
                            <input
                                type="text"
                                placeholder="Search jobs..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="pl-8 pr-7 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-700 focus:border-transparent bg-gray-50 placeholder-gray-400 w-44"
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <FiX size={12} />
                                </button>
                            )}
                        </div>

                        {/* Sort */}
                        <div className="relative" ref={refSort}>
                            <button
                                onClick={() => setOpenDropdown(p => p === "sort" ? null : "sort")}
                                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg bg-white text-gray-600 hover:border-gray-300 hover:text-zinc-700 transition-all"
                            >
                                <SortIcon className="w-3.5 h-3.5 shrink-0" />
                                <span className="hidden sm:inline">{currentSort.label}</span>
                                <FiChevronDown size={12} className={`transition-transform ${openDropdown === "sort" ? "rotate-180" : ""}`} />
                            </button>
                            {openDropdown === "sort" && (
                                <div className="absolute right-0 top-full mt-1.5 w-48 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                    {SORT_OPTIONS.map(opt => {
                                        const Icon = opt.icon;
                                        return (
                                            <button key={opt.value}
                                                onClick={() => { setSortBy(opt.value); setOpenDropdown(null); }}
                                                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm transition-colors ${sortBy === opt.value ? "bg-zinc-700 text-white font-semibold" : "text-gray-700 hover:bg-gray-50"}`}
                                            >
                                                <Icon className="w-3.5 h-3.5 shrink-0" />
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Status filter */}
                        <div className="relative" ref={refStatus}>
                            <button
                                onClick={() => setOpenDropdown(p => p === "status" ? null : "status")}
                                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-all
                                    ${statusFilter !== "all" ? "bg-zinc-700 text-white border-zinc-700" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
                            >
                                Status
                                <FiChevronDown size={12} className={`transition-transform ${openDropdown === "status" ? "rotate-180" : ""}`} />
                            </button>
                            {openDropdown === "status" && (
                                <div className="absolute right-0 top-full mt-1.5 w-52 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                    {[{ value: "all", label: "All Statuses", dot: null },
                                    ...Object.entries(STATUS_META).map(([v, m]) => ({ value: v, label: m.label, dot: m.dot }))
                                    ].map(opt => (
                                        <button key={opt.value}
                                            onClick={() => { setStatusFilter(opt.value); setOpenDropdown(null); }}
                                            className={`w-full flex items-center gap-2 px-3.5 py-2.5 text-sm transition-colors ${statusFilter === opt.value ? "bg-zinc-700 text-white font-semibold" : "text-gray-700 hover:bg-gray-50"}`}
                                        >
                                            {opt.dot && <span className={`w-2 h-2 rounded-full flex-shrink-0 ${opt.dot}`} />}
                                            <span className="flex-1 text-left">{opt.label}</span>
                                            {statusFilter === opt.value && <FiCheckCircle className="w-3.5 h-3.5 opacity-70 shrink-0" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Type filter */}
                        <div className="relative" ref={refType}>
                            <button
                                onClick={() => setOpenDropdown(p => p === "type" ? null : "type")}
                                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-lg transition-all
                                    ${jobTypeFilter !== "all" ? "bg-zinc-700 text-white border-zinc-700" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
                            >
                                Type
                                <FiChevronDown size={12} className={`transition-transform ${openDropdown === "type" ? "rotate-180" : ""}`} />
                            </button>
                            {openDropdown === "type" && (
                                <div className="absolute right-0 top-full mt-1.5 w-44 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden py-1">
                                    {[
                                        { value: "all", label: "All Types" },
                                        { value: "design_only", label: "Design Only" },
                                        { value: "estimation_only", label: "Estimation Only" },
                                        { value: "design_estimation_only", label: "Design & Est." },
                                        { value: "timber_only", label: "Timber Only" },
                                    ].map(opt => (
                                        <button key={opt.value}
                                            onClick={() => { setJobTypeFilter(opt.value); setOpenDropdown(null); }}
                                            className={`w-full flex items-center px-3.5 py-2.5 text-sm transition-colors ${jobTypeFilter === opt.value ? "bg-zinc-700 text-white font-semibold" : "text-gray-700 hover:bg-gray-50"}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {activeFilterCount > 0 && (
                            <button
                                onClick={clearAllFilters}
                                className="flex items-center gap-1 px-3 py-2 text-sm font-medium border border-red-200 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                            >
                                <FiX size={12} /> Clear ({activeFilterCount})
                            </button>
                        )}

                        <button
                            onClick={fetchJobs}
                            className="p-2 rounded-lg text-gray-400 hover:text-zinc-700 hover:bg-gray-100 transition-colors"
                            title="Refresh"
                        >
                            <FiRefreshCw size={13} />
                        </button>
                    </div>
                </div>

                {/* Column headers */}
                <div
                    className="customer-job-columns grid items-center gap-3 px-5 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0"
                    style={{ gridTemplateColumns: COL }}
                >
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Job</span>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Departments</span>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Status</span>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Address</span>
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Created</span>
                    <div />
                </div>

                {/* Scroll body */}
                <div
                    ref={listRef}
                    className="overflow-x-hidden overflow-y-scroll"
                    style={{ height: '55vh' }}
                >
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-40 gap-3">
                            <div className="w-6 h-6 border-2 border-zinc-700 border-t-transparent rounded-full animate-spin" />
                            <p className="text-sm text-gray-500">Loading jobs…</p>
                        </div>
                    ) : jobs.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-center px-4">
                            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                <Briefcase size={16} className="text-gray-400" />
                            </div>
                            <p className="text-sm font-semibold text-gray-800 mb-1">No jobs found</p>
                            <p className="text-xs text-gray-400">
                                {activeFilterCount > 0 ? "Try adjusting your filters" : "No jobs for this customer yet"}
                            </p>
                            {activeFilterCount > 0 && (
                                <button onClick={clearAllFilters} className="mt-3 text-xs font-semibold text-zinc-700 underline">
                                    Clear all filters
                                </button>
                            )}
                        </div>
                    ) : (() => {
                        const virtualItems = rowVirtualizer.getVirtualItems();
                        const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
                        const paddingBottom = virtualItems.length > 0
                            ? rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
                            : 0;
                        return (
                            <>
                                {paddingTop > 0 && <div style={{ height: `${paddingTop}px` }} />}
                                {virtualItems.map(vi => (
                                    <JobRow
                                        key={vi.key}
                                        virtualIndex={vi.index}
                                        measureElement={rowVirtualizer.measureElement}
                                        job={jobs[vi.index]}
                                        companySlug={companySlug}
                                        navigate={navigate}
                                        location={location}
                                    />
                                ))}
                                {paddingBottom > 0 && <div style={{ height: `${paddingBottom}px` }} />}
                            </>
                        );
                    })()}

                    {isLoadingMore && (
                        <div className="flex items-center justify-center py-5 gap-2">
                            <div className="w-4 h-4 border-2 border-zinc-700 border-t-transparent rounded-full animate-spin" />
                            <span className="text-xs text-gray-500">Loading more…</span>
                        </div>
                    )}
                    {!isLoading && !isLoadingMore && !pagination.hasMore && jobs.length > 0 && (
                        <p className="text-center text-[11px] text-gray-400 py-5">
                            All {pagination.total.toLocaleString()} jobs loaded
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SingleCustomer;
